import React, { Fragment, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Header from '../../components/common/global-top-bar';
import HeadBack from './redirect';
import SwitchBar from '../../components/switch-bar';
import SaveButton from './switch-bar-button';
import { HeaderOptions } from './clients-constants';
import { clientQueryKeys, useClient, useCopyClientResources } from '../../hooks/useClients';
import { Spinner, ToggleButton } from '../../components/common';
import { normalizeMobilePhone } from '../../components/common/phone-validation';
import EditClientResourceDetails from './components/resource-edit';
import { useQueryClient } from '@tanstack/react-query';
import { Resource, useResources, resourceQueryKeys } from '../../hooks/useResources';
import { Checkbox } from '@mui/material';
import { useUpdateClient } from '../../hooks/useClients';
import CopyClientResourcesModal from './components/copy-client-resources-modal';
import { useCrisisResourceOptions } from '../../hooks/useCrisisResourceOptions';

const ClientResources: React.FC = () => {
  const clientId = +localStorage.getItem('clientid')!;
  const { data: client, refetch: refetchClient } = useClient(clientId);
  const [includeHidden, setIncludeHidden] = useState(false);
  const [filter, setFilter] = useState<string>('client');
  const [enabledResourceIds, setEnabledResourceIds] = useState<number[]>(client?.enabled_resource_ids || []);
  const [isDirty, setIsDirty] = useState(false);
  const queryClient = useQueryClient();
  const { updateClient } = useUpdateClient(clientId);
  const { copyClientResources } = useCopyClientResources(clientId);
  const [showCopyModal, setShowCopyModal] = useState(false);

  const { data: resources, isLoading } = useResources(
    includeHidden,
    undefined,
    filter === 'client' ? clientId : undefined,
    true,
  );

  const switchBarSelectedTab = 4;

  const newResource = {
    name: '',
    description: '',
    long_description: '',
    meta_keywords: '',
    phone_number: '',
    url: '',
    url_description: '',
    image_url: '',
    sort_weight: 0,
    status: 'live',
    text_number: '',
    notes: '',
    client_id: clientId,
  };

  const handleAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setEnabledResourceIds(resources!.map((x) => x.id!));
    } else {
      setEnabledResourceIds([]);
    }
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, resourceId: number) => {
    if (event.target.checked) {
      setEnabledResourceIds((prev) => [...prev, resourceId]);
    } else {
      setEnabledResourceIds(enabledResourceIds?.filter((x) => x !== resourceId));
    }
  };

  const handleSave = () => {
    updateClient.mutate(
      {
        enabled_resource_ids: enabledResourceIds,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['clients']);
          setIsDirty(false);
        },
      },
    );
  };

  useEffect(() => {
    if (client && client.enabled_resource_ids) {
      setEnabledResourceIds(client.enabled_resource_ids);
    }
  }, [client]);

  const isArraysIdentical = (arr1: number[], arr2: number[]) => {
    return JSON.stringify(arr1.sort()) === JSON.stringify(arr2.sort());
  };

  useEffect(() => {
    if (enabledResourceIds && client?.enabled_resource_ids) {
      setIsDirty(!isArraysIdentical(enabledResourceIds, client?.enabled_resource_ids!));
    } else if (!client?.enabled_resource_ids) {
      setIsDirty(enabledResourceIds.length > 0);
    }
  }, [enabledResourceIds, client]);

  return (
    <div>
      <Header heading={<HeadBack clientName={client?.name || ''} />} />
      <SwitchBar
        heading={HeaderOptions}
        position={switchBarSelectedTab}
        optionalButton={
          filter === 'global' && (
            <div className="w-1/12">
              <SaveButton onSave={handleSave} disable={!isDirty} />
            </div>
          )
        }
      />
      <Filters
        client={client}
        clientId={clientId}
        omitGlobal={client?.use_global_resources}
        includeHidden={includeHidden}
        setIncludeHidden={setIncludeHidden}
        filter={filter}
        setFilter={setFilter}
        onCopyClient={() => setShowCopyModal(true)}
      />
      <CopyClientResourcesModal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        currentClientId={clientId}
        currentClientName={client?.name || ''}
        isSubmitting={copyClientResources.isLoading}
        onConfirm={async (sourceClientId) => {
          await copyClientResources.mutateAsync(sourceClientId);
          queryClient.invalidateQueries([resourceQueryKeys.resources]);
          queryClient.invalidateQueries([resourceQueryKeys.resource]);
          const updatedClient = await refetchClient();
          setEnabledResourceIds(updatedClient.data?.enabled_resource_ids ?? []);
          setIsDirty(false);
          setShowCopyModal(false);
        }}
      />
      {isLoading && <Spinner />}
      <div className="max-window-height-table overflow-y-auto px-7">
        <table className="table-row-format relative w-full table-fixed border-collapse">
          <thead className="sticky top-0 bg-white">
            <tr className="px-7 text-left">
              <th className="w-3/12">
                {filter === 'client' ? (
                  <div className="my-auto">
                    <EditClientResourceDetails resource={newResource} clientId={clientId} />
                  </div>
                ) : (
                  <div className="my-auto flex flex-nowrap items-center space-x-0.5 pl-5">
                    <div className="whitespace-nowrap">
                      <Checkbox
                        defaultChecked={false}
                        checked={enabledResourceIds && enabledResourceIds.length === resources?.length}
                        onChange={(event) => handleAllChange(event)}
                      ></Checkbox>
                      All
                    </div>
                    {enabledResourceIds.length === 0 && (
                      <div className="text-red text-xxs text-nowrap">*None selected</div>
                    )}
                  </div>
                )}
              </th>
              <th className="w-1/12"></th>
              <th className="w-3/12">Name</th>
              <th className="w-3/12">Phone</th>
              <th className="w-3/12">URL</th>
            </tr>
          </thead>
          <tbody>
            <tr className="h-2"></tr>
            {resources?.length ? (
              resources?.map((resource: Resource) => {
                return (
                  // @ts-ignore
                  <Fragment key={resource.id}>
                    <tr key={resource.id} className="text-gray-dark border-t-8 border-white bg-gray-200 text-left">
                      {filter === 'client' ? (
                        <td className="w-2/12 px-5 pt-5">
                          <EditClientResourceDetails resource={resource} clientId={clientId} />
                        </td>
                      ) : (
                        <td className="w-2/12 px-5 pt-5">
                          <Checkbox
                            defaultChecked={false}
                            checked={enabledResourceIds.includes(resource.id!)}
                            onChange={(event) => handleCheckboxChange(event, resource.id!)}
                          />
                        </td>
                      )}
                      <td className="w-2/12 pt-5">
                        {resource?.image_url ? (
                          <img width="60" src={resource?.image_url} alt="Resource Image" />
                        ) : (
                          <div className="w-24" />
                        )}
                      </td>
                      <td className="w-3/12 pt-5">
                        <div className="text-gray-dark truncate font-extrabold">{resource?.name}</div>
                      </td>
                      <td className="w-3/12 pt-5">{normalizeMobilePhone(resource?.phone_number?.replace('+1', ''))}</td>
                      <td className="w-3/12 pl-1 pt-5">
                        <div className="truncate">{resource?.url}</div>
                      </td>
                    </tr>
                    <tr className="text-gray-dark group h-6 bg-gray-200 text-left">
                      <td />
                      <td colSpan={2}>
                        <div className="max-w-2xl truncate pb-2">{resource?.description}</div>
                      </td>
                      <td colSpan={2}>
                        <div className="max-w-2xl truncate pb-2">{resource?.long_description}</div>
                      </td>
                    </tr>
                  </Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={5}>
                  <div className="flex items-center justify-center">
                    <p className="text-gray-dark text-2xl">No Resources Found</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Filters: React.FC<{
  client?: { use_global_resources?: boolean; crisis_resource_id?: number | null; name?: string };
  clientId: number;
  omitGlobal?: boolean;
  includeHidden: boolean;
  setIncludeHidden: React.Dispatch<React.SetStateAction<boolean>>;
  filter: string;
  setFilter: React.Dispatch<React.SetStateAction<string>>;
  onCopyClient: () => void;
}> = ({ client, clientId, omitGlobal, includeHidden, setIncludeHidden, filter, setFilter, onCopyClient }) => {
  const queryClient = useQueryClient();
  const { updateClient } = useUpdateClient(clientId);
  const crisisResourceOptions = useCrisisResourceOptions(clientId, client);
  const [crisisResourceId, setCrisisResourceId] = useState<string>(
    client?.crisis_resource_id != null ? String(client.crisis_resource_id) : '',
  );

  useEffect(() => {
    setCrisisResourceId(client?.crisis_resource_id != null ? String(client.crisis_resource_id) : '');
  }, [client?.crisis_resource_id]);

  const handleCrisisResourceChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    setCrisisResourceId(value);
    updateClient.mutate(
      {
        crisis_resource_id: value === '' ? null : Number(value),
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['clients']);
          toast.success('Crisis resource updated successfully!');
        },
        onError: (error: any) => {
          setCrisisResourceId(client?.crisis_resource_id != null ? String(client.crisis_resource_id) : '');
          toast.error(error.response?.data?.description ?? 'Failed to update crisis resource');
        },
      },
    );
  };

  const handleUseGlobal = (data: boolean) => {
    updateClient.mutate(
      {
        use_global_resources: data,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(['clients']);
          queryClient.invalidateQueries([resourceQueryKeys.resources]);
          queryClient.invalidateQueries([resourceQueryKeys.resource]);
          setFilter(data === false ? 'client' : 'global');
        },
      },
    );
  };

  const filterOptions = {
    client: 'Client',
    global: 'Global',
  };

  return (
    <div className="gray-border-line bg-gray-background-light w-full border-b px-7">
      <div className="flex w-full items-center justify-between py-4">
        <div className="flex items-center">
          <span className="px-3">Use Global Resources</span>{' '}
          <ToggleButton
            id={'omitGlobal'}
            status={omitGlobal}
            initialstatus={false}
            setStatus={(data: boolean) => {
              handleUseGlobal(data);
            }}
          />
        </div>
        <div className="flex items-center py-4">
          <span className="px-3">Only Hidden</span>{' '}
          <ToggleButton
            id={'includeHidden'}
            status={includeHidden}
            initialstatus={false}
            setStatus={(data: boolean) => {
              data ? setIncludeHidden(true) : setIncludeHidden(false);
            }}
          />
        </div>
        <div className="flex items-center gap-4 py-4">
          <button
            type="button"
            className="bg-blue-primary rounded-md px-4 py-2 text-sm font-semibold text-white"
            onClick={onCopyClient}
            data-testid="copy-client-resources-button"
          >
            Copy Client
          </button>
          {omitGlobal && (
            <div className="gray-background-dark flex items-center justify-between">
              {Object.keys(filterOptions).map((item: string) => (
                <button
                  key={item}
                  className={
                    'px-5 py-1 text-sm ' +
                    (item === filter ? 'bg-blue-primary text-white' : 'bg-gray-background-dark text-gray-dark')
                  }
                  onClick={() => {
                    setFilter(item);
                  }}
                >
                  {
                    // @ts-ignore
                    filterOptions[item]
                  }
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="border-t border-gray-200 pb-4 pt-3">
        <label htmlFor="crisis_resource_id" className="mb-1 block text-sm font-medium text-gray-700">
          Crisis Resource
        </label>
        <p className="mb-2 text-sm text-gray-600">
          Shown on the member crisis card when crisis is detected during search.
        </p>
        <select
          id="crisis_resource_id"
          value={crisisResourceId}
          onChange={handleCrisisResourceChange}
          data-testid="crisis_resource_id"
          className="focus:ring-bright-blue focus:border-bright-blue w-full max-w-md rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none"
        >
          <option value="">None</option>
          {crisisResourceOptions.map((resource) => (
            <option key={resource.id} value={resource.id}>
              {resource.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export default ClientResources;
